import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, MapPin, Heart, Package, LogOut, Plus, Trash2, Calendar, Award, Shield, Mail, ChevronRight, Pause, Play, X, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';
import { toast } from 'sonner';

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, API, token, user, logout, favorites } = useApp();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [recurringOrders, setRecurringOrders] = useState([]);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Set active tab based on URL
    const path = location.pathname.split('/').pop();
    if (['profile', 'addresses', 'orders', 'favorites', 'recurring', 'loyalty'].includes(path)) {
      setActiveTab(path);
    }
    
    loadData();
  }, [token, location]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [addressRes, ordersRes, favRes, recurringRes, loyaltyRes, historyRes] = await Promise.all([
        axios.get(`${API}/addresses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/recurring-orders`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { recurring_orders: [] } })),
        axios.get(`${API}/loyalty/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { loyalty_points: 0 } })),
        axios.get(`${API}/loyalty/history`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { history: [] } }))
      ]);
      setAddresses(addressRes.data.addresses);
      setOrders(ordersRes.data.orders);
      setFavoriteProducts(favRes.data.favorites);
      setRecurringOrders(recurringRes.data.recurring_orders || []);
      setLoyaltyPoints(loyaltyRes.data.loyalty_points || 0);
      setLoyaltyHistory(historyRes.data.history || []);
      setEmailVerified(user?.email_verified || false);
    } catch (error) {
      console.error('Error loading account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    try {
      await axios.delete(`${API}/addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(addresses.filter(a => a.id !== id));
      toast.success('Adres verwijderd');
    } catch (error) {
      toast.error('Adres verwijderen mislukt');
    }
  };

  const handleResendVerification = async () => {
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verificatie e-mail verzonden!');
    } catch (error) {
      toast.error('E-mail verzenden mislukt');
    }
  };

  const handlePauseRecurring = async (orderId) => {
    try {
      await axios.put(`${API}/recurring-orders/${orderId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periodieke bestelling gepauzeerd');
      loadData();
    } catch (error) {
      toast.error('Pauzeren mislukt');
    }
  };

  const handleResumeRecurring = async (orderId) => {
    try {
      await axios.put(`${API}/recurring-orders/${orderId}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periodieke bestelling hervat');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hervatten mislukt');
    }
  };

  const handleCancelRecurring = async (orderId) => {
    if (!window.confirm('Weet je zeker dat je deze periodieke bestelling wilt annuleren?')) return;
    try {
      await axios.delete(`${API}/recurring-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periodieke bestelling geannuleerd');
      loadData();
    } catch (error) {
      toast.error('Annuleren mislukt');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-500',
      processing: 'text-blue-500',
      shipped: 'text-purple-500',
      delivered: 'text-green-500',
      cancelled: 'text-red-500'
    };
    return colors[status] || 'text-gray-500';
  };

  const getStatusLabel = (status) => {
    // Use translation function
    return t(status) || status;
  };

  const getFrequencyLabel = (freq) => {
    // Use translation function
    return t(freq) || freq;
  };

  const tabs = [
    { id: 'profile', label: t('my_account'), icon: User },
    { id: 'orders', label: t('orders'), icon: Package },
    { id: 'recurring', label: t('recurring_orders'), icon: Calendar },
    { id: 'loyalty', label: t('loyalty_points'), icon: Award },
    { id: 'addresses', label: t('addresses'), icon: MapPin },
    { id: 'favorites', label: t('favorites'), icon: Heart },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8">
        <h1 className="font-heading text-3xl text-white mb-8">{t('my_account')}</h1>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass rounded-xl p-4 space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`/account/${tab.id}`);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'text-[#A8A29E] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon size={20} />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.id === 'loyalty' && loyaltyPoints > 0 && (
                    <span className="text-xs bg-[#D4AF37] text-black px-2 py-0.5 rounded-full">
                      {loyaltyPoints}
                    </span>
                  )}
                </button>
              ))}
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
                {t('logout')}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            
            {/* Email Verification Banner */}
            {!emailVerified && user && (
              <div className="glass rounded-xl p-4 mb-6 border border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={24} className="text-yellow-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-white font-medium">E-mailadres niet geverifieerd</h3>
                    <p className="text-[#A8A29E] text-sm mt-1">
                      Bevestig je e-mailadres om alle functies te gebruiken.
                    </p>
                    <button
                      onClick={handleResendVerification}
                      className="text-[#D4AF37] text-sm mt-2 hover:underline"
                    >
                      Verificatie e-mail opnieuw versturen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && user && (
              <div className="space-y-6">
                <div className="glass rounded-xl p-6">
                  <h2 className="font-heading text-xl text-white mb-6">Profielinformatie</h2>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#A8A29E] text-sm">Voornaam</label>
                        <p className="text-white">{user.first_name}</p>
                      </div>
                      <div>
                        <label className="text-[#A8A29E] text-sm">Achternaam</label>
                        <p className="text-white">{user.last_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="text-[#A8A29E] text-sm">E-mail</label>
                        <p className="text-white">{user.email}</p>
                      </div>
                      {emailVerified ? (
                        <span className="text-green-500 text-xs flex items-center gap-1">
                          <Shield size={14} /> Geverifieerd
                        </span>
                      ) : (
                        <span className="text-yellow-500 text-xs flex items-center gap-1">
                          <AlertTriangle size={14} /> Niet geverifieerd
                        </span>
                      )}
                    </div>
                    {user.phone && (
                      <div>
                        <label className="text-[#A8A29E] text-sm">Telefoon</label>
                        <p className="text-white">{user.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Section */}
                <div className="glass rounded-xl p-6">
                  <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                    <Shield size={20} className="text-[#D4AF37]" />
                    Beveiliging
                  </h2>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white">Twee-factor authenticatie (2FA)</p>
                      <p className="text-[#A8A29E] text-sm">Extra beveiliging via Microsoft Authenticator</p>
                    </div>
                    {user.is_2fa_enabled ? (
                      <span className="text-green-500 text-sm flex items-center gap-1">
                        <Shield size={16} /> Ingeschakeld
                      </span>
                    ) : (
                      <span className="text-[#A8A29E] text-sm">Niet ingeschakeld</span>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="glass rounded-xl p-6 text-center">
                    <Award size={32} className="mx-auto text-[#D4AF37] mb-2" />
                    <p className="text-2xl font-bold text-white">{loyaltyPoints}</p>
                    <p className="text-[#A8A29E] text-sm">Loyaliteitspunten</p>
                  </div>
                  <div className="glass rounded-xl p-6 text-center">
                    <Package size={32} className="mx-auto text-[#D4AF37] mb-2" />
                    <p className="text-2xl font-bold text-white">{orders.length}</p>
                    <p className="text-[#A8A29E] text-sm">Bestellingen</p>
                  </div>
                  <div className="glass rounded-xl p-6 text-center">
                    <Calendar size={32} className="mx-auto text-[#D4AF37] mb-2" />
                    <p className="text-2xl font-bold text-white">{recurringOrders.filter(r => r.is_active).length}</p>
                    <p className="text-[#A8A29E] text-sm">Actieve abonnementen</p>
                  </div>
                </div>
              </div>
            )}

            {/* Addresses Tab */}
            {activeTab === 'addresses' && (
              <div className="space-y-4">
                {addresses.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center">
                    <MapPin size={48} className="mx-auto text-[#57534E] mb-4" />
                    <p className="text-[#A8A29E]">Nog geen adressen opgeslagen</p>
                  </div>
                ) : (
                  addresses.map((address) => (
                    <div key={address.id} className="glass rounded-xl p-6 flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-white font-medium">{address.label}</h3>
                          {address.is_default && (
                            <span className="text-xs bg-[#D4AF37] text-black px-2 py-0.5 rounded">
                              Standaard
                            </span>
                          )}
                        </div>
                        <p className="text-[#A8A29E]">{address.street} {address.house_number}</p>
                        <p className="text-[#A8A29E]">{address.postal_code} {address.city}</p>
                        <p className="text-[#A8A29E]">{address.country}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteAddress(address.id)}
                        className="text-[#57534E] hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))
                )}
                <Link to="/checkout" className="btn-secondary inline-flex items-center gap-2">
                  <Plus size={18} />
                  Nieuw adres toevoegen
                </Link>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl text-white mb-4">Bestelgeschiedenis</h2>
                {orders.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center">
                    <Package size={48} className="mx-auto text-[#57534E] mb-4" />
                    <p className="text-[#A8A29E]">Nog geen bestellingen</p>
                    <Link to="/home" className="btn-primary inline-block mt-4">
                      Begin met winkelen
                    </Link>
                  </div>
                ) : (
                  orders.map((order) => (
                    <div key={order.id} className="glass rounded-xl p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="text-white font-medium">Bestelling #{order.id.slice(0, 8).toUpperCase()}</p>
                          <p className="text-[#57534E] text-xs">
                            {new Date(order.created_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                        <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-500/20 text-green-500' :
                          order.status === 'shipped' ? 'bg-purple-500/20 text-purple-500' :
                          order.status === 'processing' ? 'bg-blue-500/20 text-blue-500' :
                          order.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        {order.items.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-[#A8A29E]">
                              {item.name || item.product_name} ({item.size_ml}ml) × {item.quantity}
                            </span>
                            <span className="text-white">€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t border-[#2C1B18]">
                        <span className="text-[#D4AF37] font-bold text-lg">
                          €{order.total.toFixed(2)}
                        </span>
                        <div className="text-right">
                          {order.tracking_number && (
                            <p className="text-[#A8A29E] text-xs">
                              Tracking: {order.tracking_number}
                            </p>
                          )}
                          {order.shipping_method === 'express' && (
                            <p className="text-[#D4AF37] text-xs">Direct Delivery</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Recurring Orders Tab */}
            {activeTab === 'recurring' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="font-heading text-xl text-white">Periodieke Bestellingen</h2>
                  <Link to="/home" className="btn-primary text-sm">
                    <Plus size={16} className="inline mr-1" />
                    Nieuw abonnement
                  </Link>
                </div>
                
                <div className="glass rounded-xl p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                  <p className="text-[#A8A29E] text-sm">
                    <strong className="text-white">Periodieke bestellingen</strong> zijn ideaal voor je favoriete parfums. 
                    Stel in hoe vaak je wilt dat we leveren en betaal vooraf. Je bespaart tijd en bent altijd voorzien!
                  </p>
                </div>

                {recurringOrders.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center">
                    <Calendar size={48} className="mx-auto text-[#57534E] mb-4" />
                    <p className="text-[#A8A29E]">Nog geen periodieke bestellingen</p>
                    <p className="text-[#57534E] text-sm mt-2">
                      Stel een abonnement in voor je favoriete parfums
                    </p>
                  </div>
                ) : (
                  recurringOrders.map((recurring) => (
                    <div key={recurring.id} className="glass rounded-xl p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-medium">
                              {getFrequencyLabel(recurring.frequency)} levering
                            </h3>
                            {recurring.is_active ? (
                              <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded">
                                Actief
                              </span>
                            ) : (
                              <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded">
                                Gepauzeerd
                              </span>
                            )}
                          </div>
                          <p className="text-[#57534E] text-xs mt-1">
                            Volgende levering: {new Date(recurring.next_order_date).toLocaleDateString('nl-NL')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {recurring.is_active ? (
                            <button
                              onClick={() => handlePauseRecurring(recurring.id)}
                              className="p-2 text-[#A8A29E] hover:text-yellow-500 transition-colors"
                              title="Pauzeren"
                            >
                              <Pause size={18} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleResumeRecurring(recurring.id)}
                              className="p-2 text-[#A8A29E] hover:text-green-500 transition-colors"
                              title="Hervatten"
                            >
                              <Play size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleCancelRecurring(recurring.id)}
                            className="p-2 text-[#A8A29E] hover:text-red-500 transition-colors"
                            title="Annuleren"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {recurring.items.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-[#A8A29E]">
                              {item.product_name || item.name} ({item.size_ml}ml) × {item.quantity}
                            </span>
                            <span className="text-white">€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t border-[#2C1B18]">
                        <div>
                          <span className="text-[#D4AF37] font-bold">
                            €{recurring.total_per_order?.toFixed(2) || '0.00'} per levering
                          </span>
                          <p className="text-[#57534E] text-xs">
                            {recurring.prepaid_orders_remaining} vooruitbetaalde leveringen over
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Loyalty Points Tab */}
            {activeTab === 'loyalty' && (
              <LoyaltySection 
                API={API}
                token={token}
                loyaltyPoints={loyaltyPoints}
                loyaltyHistory={loyaltyHistory}
                onUpdate={loadData}
              />
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoriteProducts.length === 0 ? (
                  <div className="col-span-full glass rounded-xl p-6 text-center">
                    <Heart size={48} className="mx-auto text-[#57534E] mb-4" />
                    <p className="text-[#A8A29E]">Nog geen favorieten</p>
                    <Link to="/home" className="btn-primary inline-block mt-4">
                      Ontdek parfums
                    </Link>
                  </div>
                ) : (
                  favoriteProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="glass rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors"
                    >
                      <div className="aspect-square bg-[#121212]">
                        <img 
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <p className="text-[#D4AF37] text-xs uppercase tracking-wider">
                          {product.brand}
                        </p>
                        <h3 className="text-white font-heading">{product.name}</h3>
                        <p className="text-[#D4AF37] font-bold mt-2">
                          €{product.price.toFixed(2)}
                        </p>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Loyalty Section Component
const LoyaltySection = ({ API, token, loyaltyPoints, loyaltyHistory, onUpdate }) => {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [redeemAmount, setRedeemAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  useEffect(() => {
    loadDiscountCodes();
  }, []);

  const loadDiscountCodes = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountCodes(response.data.discount_codes || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    }
  };

  const handleRedeem = async () => {
    if (redeemAmount > loyaltyPoints) {
      toast.error('Onvoldoende punten');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/loyalty/redeem`, 
        { points_to_redeem: redeemAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowRedeemModal(false);
      loadDiscountCodes();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inwisselen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Kortingscode gekopieerd!');
  };

  const maxRedeemable = Math.floor(loyaltyPoints / 100) * 100;
  const discountValue = (redeemAmount / 100) * 5;

  // Tier colors and icons
  const tierConfig = {
    bronze: { color: '#CD7F32', bgColor: 'bg-[#CD7F32]/10', borderColor: 'border-[#CD7F32]/30' },
    silver: { color: '#C0C0C0', bgColor: 'bg-[#C0C0C0]/10', borderColor: 'border-[#C0C0C0]/30' },
    gold: { color: '#D4AF37', bgColor: 'bg-[#D4AF37]/10', borderColor: 'border-[#D4AF37]/30' },
    platinum: { color: '#E5E4E2', bgColor: 'bg-[#E5E4E2]/10', borderColor: 'border-[#E5E4E2]/30' }
  };

  const [tierInfo, setTierInfo] = useState(null);

  useEffect(() => {
    const loadTierInfo = async () => {
      try {
        const response = await axios.get(`${API}/loyalty/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTierInfo(response.data.tier);
      } catch (error) {
        console.error('Error loading tier info:', error);
      }
    };
    loadTierInfo();
  }, [API, token]);

  const currentTierConfig = tierInfo ? tierConfig[tierInfo.current_tier] : tierConfig.bronze;

  return (
    <div className="space-y-6">
      {/* Tier Card */}
      {tierInfo && (
        <div className={`glass rounded-xl p-6 border ${currentTierConfig.borderColor}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className={`w-12 h-12 rounded-full ${currentTierConfig.bgColor} flex items-center justify-center`}
                style={{ boxShadow: `0 0 20px ${currentTierConfig.color}40` }}
              >
                <Award size={24} style={{ color: currentTierConfig.color }} />
              </div>
              <div>
                <p className="text-white font-heading text-xl">{tierInfo.tier_name_nl}</p>
                <p className="text-[#A8A29E] text-sm">Lid Status</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white font-bold text-2xl">{loyaltyPoints}</p>
              <p className="text-[#A8A29E] text-sm">punten beschikbaar</p>
            </div>
          </div>
          
          {/* Progress to next tier */}
          {tierInfo.next_tier && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#A8A29E]">Voortgang naar {tierConfig[tierInfo.next_tier]?.color ? tierInfo.next_tier.charAt(0).toUpperCase() + tierInfo.next_tier.slice(1) : 'volgende'}</span>
                <span className="text-white">{tierInfo.points_to_next_tier} punten te gaan</span>
              </div>
              <div className="h-2 bg-[#2C1B18] rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${tierInfo.progress_percent}%`,
                    background: `linear-gradient(90deg, ${currentTierConfig.color}, ${tierConfig[tierInfo.next_tier]?.color || currentTierConfig.color})`
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Current Benefits */}
          <div className="mt-4 pt-4 border-t border-[#2C1B18]">
            <p className="text-[#A8A29E] text-sm mb-2">Jouw voordelen:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {tierInfo.benefits.points_multiplier > 1 && (
                <div className="flex items-center gap-2 text-[#A8A29E]">
                  <span style={{ color: currentTierConfig.color }}>✓</span>
                  {Math.round((tierInfo.benefits.points_multiplier - 1) * 100)}% extra punten
                </div>
              )}
              {tierInfo.benefits.discount_rate > 0 && (
                <div className="flex items-center gap-2 text-[#A8A29E]">
                  <span style={{ color: currentTierConfig.color }}>✓</span>
                  {tierInfo.benefits.discount_rate}% extra korting
                </div>
              )}
              <div className="flex items-center gap-2 text-[#A8A29E]">
                <span style={{ color: currentTierConfig.color }}>✓</span>
                {tierInfo.benefits.free_shipping_threshold === 0 
                  ? 'Gratis verzending' 
                  : `Gratis vanaf €${tierInfo.benefits.free_shipping_threshold}`}
              </div>
              {tierInfo.benefits.birthday_bonus > 0 && (
                <div className="flex items-center gap-2 text-[#A8A29E]">
                  <span style={{ color: currentTierConfig.color }}>✓</span>
                  {tierInfo.benefits.birthday_bonus} verjaardag punten
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Points Balance Card */}
      <div className="glass rounded-xl p-6 text-center">
        <p className="text-4xl font-bold text-white mb-2">{loyaltyPoints}</p>
        <p className="text-[#A8A29E]">Beschikbare punten</p>
        <p className="text-[#57534E] text-sm mt-2">
          100 punten = €5 korting
        </p>
        
        {loyaltyPoints >= 100 && (
          <button
            onClick={() => setShowRedeemModal(true)}
            className="btn-primary mt-4"
            data-testid="redeem-points-btn"
          >
            Punten Inwisselen
          </button>
        )}
      </div>

      {/* Active Discount Codes */}
      {discountCodes.length > 0 && (
        <div className="glass rounded-xl p-6">
          <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
            <Award className="text-[#D4AF37]" size={20} />
            Jouw Kortingscodes
          </h3>
          <div className="space-y-3">
            {discountCodes.map((discount) => (
              <div key={discount.id} className="flex items-center justify-between p-3 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
                <div>
                  <p className="text-white font-mono font-bold">{discount.code}</p>
                  <p className="text-[#D4AF37] text-sm">€{discount.discount_amount.toFixed(2)} korting</p>
                  <p className="text-[#57534E] text-xs">
                    Geldig tot {new Date(discount.expires_at).toLocaleDateString('nl-NL')}
                  </p>
                </div>
                <button
                  onClick={() => copyCode(discount.code)}
                  className="btn-secondary text-sm px-3 py-1"
                >
                  Kopieer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Points History */}
      <div className="glass rounded-xl p-6">
        <h3 className="font-heading text-lg text-white mb-4">Puntengeschiedenis</h3>
        {loyaltyHistory.length === 0 ? (
          <p className="text-[#A8A29E] text-center py-4">Nog geen puntentransacties</p>
        ) : (
          <div className="space-y-3">
            {loyaltyHistory.map((transaction) => (
              <div key={transaction.id} className="flex justify-between items-center py-2 border-b border-[#2C1B18] last:border-0">
                <div>
                  <p className="text-white text-sm">{transaction.description}</p>
                  <p className="text-[#57534E] text-xs">
                    {new Date(transaction.created_at).toLocaleDateString('nl-NL')}
                  </p>
                </div>
                <span className={`font-bold ${
                  transaction.type === 'earned' ? 'text-green-500' : 
                  transaction.type === 'redeemed' ? 'text-[#D4AF37]' : 
                  'text-red-500'
                }`}>
                  {transaction.points > 0 ? '+' : ''}{transaction.points}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How it Works */}
      <div className="glass rounded-xl p-6 border border-[#D4AF37]/30">
        <h3 className="font-heading text-lg text-white mb-4">Hoe werkt het?</h3>
        <ul className="space-y-2 text-[#A8A29E] text-sm">
          <li className="flex items-start gap-2">
            <span className="text-[#D4AF37]">1.</span>
            Verdien 1 punt voor elke euro die je uitgeeft
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#D4AF37]">2.</span>
            Punten worden automatisch toegevoegd na een succesvolle bestelling
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#D4AF37]">3.</span>
            Wissel 100 punten in voor €5 korting
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#D4AF37]">4.</span>
            Gebruik je kortingscode bij je volgende bestelling
          </li>
        </ul>
      </div>

      {/* Redeem Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="glass p-8 rounded-2xl max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowRedeemModal(false)}
              className="absolute top-4 right-4 text-[#A8A29E] hover:text-white"
            >
              <X size={24} />
            </button>
            
            <h2 className="font-heading text-2xl text-white mb-2 text-center">Punten Inwisselen</h2>
            <p className="text-[#A8A29E] text-center mb-6">Je hebt {loyaltyPoints} punten</p>
            
            <div className="space-y-6">
              <div>
                <label className="text-[#A8A29E] text-sm block mb-2">Hoeveel punten wil je inwisselen?</label>
                <select
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(Number(e.target.value))}
                  className="w-full bg-[#121212] border border-[#2C1B18] rounded-lg px-4 py-3 text-white"
                  data-testid="redeem-amount-select"
                >
                  {Array.from({ length: Math.floor(maxRedeemable / 100) }, (_, i) => (i + 1) * 100).map((amount) => (
                    <option key={amount} value={amount}>
                      {amount} punten = €{(amount / 100 * 5).toFixed(2)} korting
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="bg-[#D4AF37]/10 rounded-lg p-4 border border-[#D4AF37]/30">
                <div className="flex justify-between mb-2">
                  <span className="text-[#A8A29E]">Punten in te wisselen</span>
                  <span className="text-white font-bold">{redeemAmount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A8A29E]">Korting ontvangen</span>
                  <span className="text-[#D4AF37] font-bold">€{discountValue.toFixed(2)}</span>
                </div>
              </div>
              
              <button
                onClick={handleRedeem}
                disabled={loading || redeemAmount > loyaltyPoints}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="confirm-redeem-btn"
              >
                {loading ? <div className="spinner mx-auto"></div> : `Inwisselen voor €${discountValue.toFixed(2)}`}
              </button>
              
              <p className="text-[#57534E] text-xs text-center">
                Je kortingscode is 90 dagen geldig
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
